/**
 * Zod schemas for content piece structure.
 * Ensures title, excerpt, and body conform to expected shapes when saving edits.
 */

import { z } from 'zod'

/** Payload for saving a content version (edit form). */
export const ContentVersionPayloadSchema = z.object({
  body: z
    .string()
    .min(1, 'El cuerpo del contenido es obligatorio'),
  title: z
    .string()
    .max(500, 'El título no puede superar 500 caracteres')
    .optional()
    .nullable()
    .transform((v) => (v === '' ? undefined : v ?? undefined)),
  excerpt: z
    .string()
    .optional()
    .nullable()
    .transform((v) => (v === '' ? undefined : v ?? undefined)),
  cta: z
    .string()
    .max(500)
    .optional()
    .nullable()
    .transform((v) => (v === '' ? undefined : v ?? undefined)),
})

export type ContentVersionPayload = z.infer<typeof ContentVersionPayloadSchema>

/**
 * Validates and normalizes the edit payload before sending to the API.
 * Returns { success: true, data } or { success: false, errors }.
 */
export function validateContentVersionPayload(
  raw: { body: string; title?: string | null; excerpt?: string | null; cta?: string | null }
): { success: true; data: ContentVersionPayload } | { success: false; errors: Record<string, string> } {
  const result = ContentVersionPayloadSchema.safeParse(raw)
  if (result.success) {
    return { success: true, data: result.data }
  }
  const errors: Record<string, string> = {}
  const flattened = result.error.flatten()
  for (const [key, messages] of Object.entries(flattened.fieldErrors)) {
    const msg = messages?.[0]
    if (key && msg) errors[key] = msg
  }
  return { success: false, errors }
}
